import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
// api
import ApiKey from '../../Components/api/ApiKey';
import BaseApi from '../../Components/api/BaseApi';
// components
import SmallLoading from '../../Components/element/SmallLoading';
import CVDescription from '../../Components/element/CVDescription';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../../Components/helpers';
// hooks
import { useWindowSize } from '../../Hooks/use-window-size';

import { Button, Flex } from '@radix-ui/themes';
import { EditIcon, IconWest, ContentCopyIcon, DownloadIcon, DeleteIcon } from 'shared/icons';
import './JSMyCV.scss';

export const JSMyCV = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const { width } = useWindowSize();
   const { t } = useTranslation('global');
   const userType = Cookies.get('user_type');
   const tokenKey = Cookies.get('tokenClient');

   const [loading, setLoading] = useState(true);
   const [cvDetails, setCvDetails] = useState({});
   const [smallLoading, setSmallLoading] = useState(false);

   const getData = async () => {
      try {
         setLoading(true);

         const response = await axios.post(
            BaseApi + '/candidates/getCv',
            { id },
            {
               headers: {
                  key: ApiKey,
                  token: tokenKey,
                  contentType: 'application/json',
               },
            }
         );

         setLoading(false);

         if (response.data.status === 200) {
            setCvDetails(response.data.response);
         } else if (response.data.status === 400) {
            navigate('/');

            Cookies.remove('fname');
            Cookies.remove('user_id');
            Cookies.remove('user_type');
            Cookies.remove('tokenClient');

            resultInfo('warning', response.data.message, t('jobseekerManageAlert.close'));
         } else {
            resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
         }
      } catch (error) {
         setLoading(false);

         if (
            error.message === 'Network Error' ||
            error.response.data.message === 'Token has expired'
         ) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         } else if (error.response.status === 403) {
            Swal.fire({
               icon: 'warning',
               title: error.response.data.message,
               confirmButtonText: t('jobseekerManageAlert.close'),
            }).then((result) => {
               if (result.isConfirmed) {
                  navigate('/');
               }
            });
         }

         console.error('Cannot get profile data', error);
      }
   };

   const handleDownloadResume = async () => {
      try {
         setSmallLoading(true);

         const response = await axios.post(
            BaseApi + '/download-user-pdf',
            {
               id,
               contacts: true,
            },
            {
               responseType: 'blob',
               headers: {
                  key: ApiKey,
                  token: tokenKey,
               },
            }
         );

         setSmallLoading(false);

         const url = window.URL.createObjectURL(response.data);
         const filename = response.headers['content-disposition']
            ? response.headers['content-disposition'].split('filename=')[1]
            : 'resume-grc.pdf';

         const link = document.createElement('a');
         link.href = url;
         link.setAttribute('download', filename.replace(/['"]/g, ''));
         document.body.appendChild(link);
         link.click();
         window.URL.revokeObjectURL(url);
         document.body.removeChild(link);
      } catch (error) {
         setSmallLoading(false);

         if (error.response.status === 400) {
            resultInfo(
               'warning',
               t('jobseekerManageAlert.contactsError'),
               t('jobseekerManageAlert.close')
            );
         } else {
            resultInfo(
               'error',
               t('jobDescription.unsubscribeError'),
               t('jobseekerManageAlert.close')
            );
         }

         console.error('Cannot download resume', error);
      }
   };

   useEffect(() => {
      if (userType === 'recruiter' || !tokenKey) {
         navigate('/auth/candidate-login');
      } else {
         getData();
         window.scrollTo(0, 0);
      }
   }, [tokenKey, navigate, userType]); // eslint-disable-line

   return (
      <div className="row">
         {loading ? (
            <div className="loader-container loader--centered"></div>
         ) : (
            <>
               <div className="container JSMyCV">
                  <Flex className="js-my-cv-actions" justify="between" gap="4" wrap="wrap">
                     <Button
                        asChild
                        color="blue"
                        size="3"
                        radius="small"
                        highContrast
                        variant="outline"
                     >
                        <Link to="/candidate/my-cv-list">
                           <IconWest width={20} height={20} />
                           {width >= 768 && t('cvDescription.back')}
                        </Link>
                     </Button>

                     <Button asChild size="3" radius="small">
                        <Link to={`/candidate/resume/${id}/edit`}>
                           <EditIcon width={20} height={20} />
                           {width >= 768 && t('cvDescription.edit')}
                        </Link>
                     </Button>

                     <Button asChild size="3" radius="small">
                        <Link to={`/candidate/resume/${id}/copy`}>
                           <ContentCopyIcon width={20} height={20} />
                           {width >= 768 && t('cvDescription.copy')}
                        </Link>
                     </Button>

                     <Button
                        size="3"
                        radius="small"
                        loading={smallLoading}
                        onClick={() => handleDownloadResume()}
                     >
                        <DownloadIcon width={20} height={20} />
                        {width >= 768 && t('cvDescription.downloadPDF')}{' '}
                     </Button>

                     <Button size="3" radius="small" disabled>
                        <DeleteIcon width={20} height={20} />
                        {width >= 768 && t('cvDescription.delete')}
                     </Button>
                  </Flex>
                  <CVDescription cvDetails={cvDetails} />
               </div>
            </>
         )}
      </div>
   );
};

export default JSMyCV;
